import React from 'react';
import { Container, Title, Text, Stack, Anchor, Paper, List } from '@mantine/core';

/**
 * Terms & Conditions Page
 *
 * Displays the terms and conditions for MentionedMe.
 *
 * SESSION_210: Initial implementation
 * SESSION_224: Added MarketingHeader for navigation
 * SPRINT_039: Updated for production readiness with MentionedMe-specific details
 * Sprint 041: Removed MarketingHeader/Footer (now provided by PublicLayout)
 */
export const TermsPage: React.FC = (): React.ReactElement => {
  const lastUpdated = 'February 2026';

  return (
    <Container size="md" py="xl">
        <Paper p="xl" withBorder>
          <Stack gap="lg">
            <Title order={1}>Terms & Conditions</Title>
            <Text c="dimmed" size="sm">Last updated: {lastUpdated}</Text>

            <Stack gap="md">
              <section>
                <Title order={2} size="h3" mb="xs">1. Agreement to Terms</Title>
                <Text>
                  By accessing or using MentionedMe (&quot;the Service&quot;), you agree to be bound by these
                  Terms and Conditions. If you disagree with any part of these terms, you may not access
                  the Service.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">2. Description of Service</Title>
                <Text>
                  MentionedMe is a social media monitoring platform that helps business owners discover when their
                  businesses are mentioned in public discussions on Reddit. The Service includes:
                </Text>
                <List mt="xs">
                  <List.Item>Automated monitoring of public Reddit discussions in local community subreddits</List.Item>
                  <List.Item>Notification alerts when your business is mentioned</List.Item>
                  <List.Item>A web dashboard to view, manage, and respond to mentions</List.Item>
                  <List.Item>Analytics and reporting tools</List.Item>
                </List>
                <Text mt="xs">
                  We analyze publicly available content only. We do not have access to private messages,
                  password-protected content, or non-public Reddit posts.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">3. User Accounts</Title>
                <Text mb="xs">When you create an account with us, you must:</Text>
                <List>
                  <List.Item>Provide accurate, complete, and current information</List.Item>
                  <List.Item>Maintain the security of your password and account</List.Item>
                  <List.Item>Notify us immediately of any unauthorized access</List.Item>
                  <List.Item>Be at least 18 years old or have parental consent</List.Item>
                </List>
                <Text mt="xs">
                  You are responsible for all activities that occur under your account.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">4. Business Claiming</Title>
                <Text>
                  By claiming a business on our platform, you represent and warrant that you are authorized
                  to act on behalf of that business. We reserve the right to verify your authorization and
                  remove claims that are fraudulent or unauthorized.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">5. Subscription and Payment</Title>
                <Text mb="xs">By subscribing to our paid services:</Text>
                <List>
                  <List.Item>
                    You agree to pay all applicable fees at the rates displayed at the time of subscription
                  </List.Item>
                  <List.Item>
                    Subscriptions are billed monthly in advance unless otherwise specified
                  </List.Item>
                  <List.Item>
                    You authorize us to charge your payment method on a recurring basis through our payment
                    processor, Stripe
                  </List.Item>
                  <List.Item>
                    You may cancel your subscription at any time through your account settings
                  </List.Item>
                  <List.Item>
                    Cancellations take effect at the end of the current billing period; you will retain
                    access until then
                  </List.Item>
                  <List.Item>
                    No prorated refunds are provided for partial months
                  </List.Item>
                </List>
                <Text mt="xs">
                  <strong>Refund Policy:</strong> Refunds are considered on a case-by-case basis within
                  14 days of initial subscription. Contact{' '}
                  <Anchor href="mailto:support@mentionedme.com">support@mentionedme.com</Anchor> for refund requests.
                  After 14 days, subscriptions are non-refundable but may be cancelled at any time.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">6. Acceptable Use</Title>
                <Text mb="xs">You agree not to:</Text>
                <List>
                  <List.Item>Use the Service for any illegal purpose</List.Item>
                  <List.Item>Violate any laws or regulations</List.Item>
                  <List.Item>Infringe on the rights of others</List.Item>
                  <List.Item>Attempt to gain unauthorized access to any part of the Service</List.Item>
                  <List.Item>Interfere with or disrupt the Service or servers</List.Item>
                  <List.Item>Use the Service to send spam or unsolicited communications</List.Item>
                  <List.Item>Impersonate another person or entity</List.Item>
                  <List.Item>Use automated means to access the Service without permission</List.Item>
                </List>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">7. Intellectual Property</Title>
                <Text>
                  The Service and its original content, features, and functionality are owned by MentionedMe
                  and are protected by international copyright, trademark, patent, trade secret, and other
                  intellectual property laws. You may not copy, modify, distribute, sell, or lease any
                  part of our Service without prior written consent.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">8. Third-Party Content</Title>
                <Text>
                  Our Service displays content from Reddit and other third-party sources. This content
                  is publicly available and we do not claim ownership of it. We are not responsible for
                  the accuracy, completeness, or legality of third-party content. All such content
                  remains the property of its respective authors and platforms.
                </Text>
                <Text mt="xs">
                  Reddit content displayed through our Service is subject to Reddit&apos;s terms of service
                  and content policies. If you believe content displayed on our platform violates your
                  rights, please contact us at{' '}
                  <Anchor href="mailto:legal@mentionedme.com">legal@mentionedme.com</Anchor>.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">9. Disclaimer of Warranties</Title>
                <Text>
                  THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
                  EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED,
                  SECURE, OR ERROR-FREE. WE MAKE NO WARRANTIES REGARDING THE ACCURACY OR COMPLETENESS OF
                  ANY MENTION DATA OR ANALYTICS.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">10. Limitation of Liability</Title>
                <Text>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, MENTIONEDME SHALL NOT BE LIABLE FOR ANY INDIRECT,
                  INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
                  WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER
                  INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">11. Indemnification</Title>
                <Text>
                  You agree to indemnify and hold harmless MentionedMe and its officers, directors, employees,
                  and agents from any claims, damages, losses, liabilities, and expenses arising out of your
                  use of the Service or violation of these Terms.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">12. Termination</Title>
                <Text>
                  We may terminate or suspend your account and access to the Service immediately, without
                  prior notice or liability, for any reason, including breach of these Terms. Upon termination,
                  your right to use the Service will immediately cease.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">13. Changes to Terms</Title>
                <Text>
                  We reserve the right to modify these Terms at any time. We will provide notice of significant
                  changes by updating the &quot;Last updated&quot; date and, where appropriate, notifying you by email.
                  Your continued use of the Service after changes constitutes acceptance of the new Terms.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">14. Governing Law</Title>
                <Text>
                  These Terms shall be governed by and construed in accordance with the laws of the
                  State of Washington, United States, without regard to its conflict of law provisions.
                  Any disputes arising from these Terms or your use of the Service shall be resolved
                  in the courts located in King County, Washington.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">15. Dispute Resolution</Title>
                <Text>
                  Before filing a claim, you agree to try to resolve the dispute informally by contacting
                  us at <Anchor href="mailto:legal@mentionedme.com">legal@mentionedme.com</Anchor>. We will
                  attempt to resolve the dispute through good faith negotiations within 30 days. If the
                  dispute cannot be resolved informally, you agree to binding arbitration administered
                  by the American Arbitration Association under its Consumer Arbitration Rules, except
                  that either party may seek injunctive or equitable relief in court.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">16. Severability</Title>
                <Text>
                  If any provision of these Terms is found to be unenforceable or invalid, that provision
                  shall be limited or eliminated to the minimum extent necessary so that these Terms
                  shall otherwise remain in full force and effect.
                </Text>
              </section>

              <section>
                <Title order={2} size="h3" mb="xs">17. Contact Us</Title>
                <Text>
                  If you have any questions about these Terms, please contact us:
                </Text>
                <List mt="xs">
                  <List.Item>Email: <Anchor href="mailto:legal@mentionedme.com">legal@mentionedme.com</Anchor></List.Item>
                  <List.Item>Support: <Anchor href="mailto:support@mentionedme.com">support@mentionedme.com</Anchor></List.Item>
                  <List.Item>Website: <Anchor href="https://mentionedme.com/contact">mentionedme.com/contact</Anchor></List.Item>
                </List>
              </section>
            </Stack>
          </Stack>
        </Paper>
    </Container>
  );
};

export default TermsPage;
